import React, { useEffect, useState } from 'react';
import { 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText, 
  Box, 
  Chip, 
  OutlinedInput, 
  SelectChangeEvent,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { 
  getManufacturers, 
  getMachineTypes, 
  getSensors, 
  getSimulationInfo,
  Manufacturer,
  MachineType,
  Sensor,
  SimulationInfo,
} from '../services/backendApi';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export type ContentType = 'documentation' | 'training' | 'simulation' | 'templates';

export interface DocumentationData {
  sensorId: string;
  assetId: string;
  manufacturer: string;
  model: string;
  outputs: string[];
}

export interface TrainingData {
  topic: string;
  audience: string;
  format: string[];
}

export interface SimulationData {
  scenario: string;
  duration: string;
  resolution: string;
  synthesize: boolean;
  // New fields for DB-based selection
  manufacturerId?: string;
  machineTypeId?: string;
  outputFormats: string[];
  // 2025 Digital Twin Standards
  fidelity: string; // Digital Twin Fidelity Level
  targetSystem: string; // Zielsystem (NVIDIA Omniverse, MATLAB/Simulink, Azure Digital Twins, etc.)
  anomalyType?: string; // Erweiterte Anomalie-Typen
}

export interface TemplatesData {
  templateType: string;
  industry: string;
}

export type FormData = DocumentationData | TrainingData | SimulationData | TemplatesData;

// ─────────────────────────────────────────────────────────────────────────────
// Validation helpers
// ─────────────────────────────────────────────────────────────────────────────
export interface ValidationErrors {
  [key: string]: string | undefined;
}

export function validateDocumentation(data: DocumentationData): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!data.sensorId.trim()) errors.sensorId = 'Sensor-ID ist erforderlich';
  if (!data.assetId.trim()) errors.assetId = 'Asset-ID ist erforderlich';
  if (!data.manufacturer.trim()) errors.manufacturer = 'Hersteller ist erforderlich';
  if (!data.model.trim()) errors.model = 'Modell ist erforderlich';
  if (data.outputs.length === 0) errors.outputs = 'Mindestens ein Ausgabeformat auswählen';
  return errors;
}

export function validateTraining(data: TrainingData): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!data.topic.trim()) errors.topic = 'Thema ist erforderlich';
  if (!data.audience.trim()) errors.audience = 'Zielgruppe ist erforderlich';
  if (data.format.length === 0) errors.format = 'Mindestens ein Format auswählen';
  return errors;
}

export function validateSimulation(data: SimulationData): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!data.scenario.trim()) errors.scenario = 'Szenario ist erforderlich';
  if (!data.duration.trim()) errors.duration = 'Dauer ist erforderlich';
  if (!data.resolution.trim()) errors.resolution = 'Auflösung ist erforderlich';
  return errors;
}

export function validateTemplates(data: TemplatesData): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!data.templateType.trim()) errors.templateType = 'Template-Typ ist erforderlich';
  if (!data.industry.trim()) errors.industry = 'Branche ist erforderlich';
  return errors;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default values
// ─────────────────────────────────────────────────────────────────────────────
export const defaultDocumentation: DocumentationData = {
  sensorId: '',
  assetId: '',
  manufacturer: '',
  model: '',
  outputs: ['DITA'],
};

export const defaultTraining: TrainingData = {
  topic: '',
  audience: '',
  format: ['Slides'],
};

export const defaultSimulation: SimulationData = {
  scenario: '',
  duration: '15min',
  resolution: '1s',
  synthesize: true,
  manufacturerId: '',
  machineTypeId: '',
  outputFormats: ['JSON', 'AASX'],
  fidelity: 'High (Operational)',
  targetSystem: 'Azure Digital Twins',
  anomalyType: '',
};

export const defaultTemplates: TemplatesData = {
  templateType: 'DITA',
  industry: '',
};

// ─────────────────────────────────────────────────────────────────────────────
// Form Components
// ─────────────────────────────────────────────────────────────────────────────

interface FormProps<T> {
  data: T;
  onChange: (data: T) => void;
  errors: ValidationErrors;
}

export const DocumentationForm: React.FC<FormProps<DocumentationData>> = ({ data, onChange, errors }) => {
  const outputOptions = ['DITA', 'S1000D', 'AASX', 'VDI 2770', 'Markdown', 'PDF'];
  const [manufacturers, setManufacturers] = React.useState<Array<{id: string, name: string}>>([]);
  const [machineTypes, setMachineTypes] = React.useState<Array<{id: string, name: string, manufacturer_id: string}>>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        setLoading(true);
        const data = await getManufacturers();
        setManufacturers(data);
      } catch (error) {
        console.error('Failed to load manufacturers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchManufacturers();
  }, []);

  React.useEffect(() => {
    const fetchMachineTypes = async () => {
      if (!data.manufacturer) {
        setMachineTypes([]);
        return;
      }
      try {
        const selectedManufacturer = manufacturers.find(m => m.name === data.manufacturer);
        if (selectedManufacturer) {
          const allTypes = await getMachineTypes(selectedManufacturer.id);
          setMachineTypes(allTypes);
        }
      } catch (error) {
        console.error('Failed to load machine types:', error);
      }
    };
    fetchMachineTypes();
  }, [data.manufacturer, manufacturers]);

  const handleMultiSelect = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange({ ...data, outputs: typeof value === 'string' ? value.split(',') : value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Sensor-ID"
        value={data.sensorId}
        onChange={(e) => onChange({ ...data, sensorId: e.target.value })}
        error={!!errors.sensorId}
        helperText={errors.sensorId || 'z.B. SENSOR-123'}
        fullWidth
        required
      />
      <TextField
        label="Asset-ID"
        value={data.assetId}
        onChange={(e) => onChange({ ...data, assetId: e.target.value })}
        error={!!errors.assetId}
        helperText={errors.assetId || 'z.B. ASSET-456'}
        fullWidth
        required
      />
      <FormControl fullWidth required error={!!errors.manufacturer}>
        <InputLabel>Hersteller</InputLabel>
        <Select
          value={data.manufacturer}
          onChange={(e) => {
            onChange({ ...data, manufacturer: e.target.value, model: '' });
          }}
          label="Hersteller"
          disabled={loading}
        >
          {manufacturers.map((manufacturer) => (
            <MenuItem key={manufacturer.id} value={manufacturer.name}>
              {manufacturer.name}
            </MenuItem>
          ))}
        </Select>
        {errors.manufacturer && <FormHelperText>{errors.manufacturer}</FormHelperText>}
      </FormControl>
      <FormControl fullWidth required error={!!errors.model} disabled={!data.manufacturer}>
        <InputLabel>Modell</InputLabel>
        <Select
          value={data.model}
          onChange={(e) => onChange({ ...data, model: e.target.value })}
          label="Modell"
        >
          {machineTypes.length === 0 && data.manufacturer && (
            <MenuItem value="" disabled>
              Keine Modelle verfügbar
            </MenuItem>
          )}
          {machineTypes.map((type) => (
            <MenuItem key={type.id} value={type.name}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
        {errors.model && <FormHelperText>{errors.model}</FormHelperText>}
        {!data.manufacturer && <FormHelperText>Bitte zuerst Hersteller auswählen</FormHelperText>}
      </FormControl>
      <FormControl fullWidth error={!!errors.outputs}>
        <InputLabel>Ausgabeformate</InputLabel>
        <Select
          multiple
          value={data.outputs}
          onChange={handleMultiSelect}
          input={<OutlinedInput label="Ausgabeformate" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {outputOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
        {errors.outputs && <FormHelperText>{errors.outputs}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export const TrainingForm: React.FC<FormProps<TrainingData>> = ({ data, onChange, errors }) => {
  const formatOptions = ['Video', 'Slides', 'Interactive', 'PDF', 'Quiz'];
  const audienceOptions = ['Techniker', 'Manager', 'Entwickler', 'Einsteiger'];

  const handleFormatChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange({ ...data, format: typeof value === 'string' ? value.split(',') : value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Thema"
        value={data.topic}
        onChange={(e) => onChange({ ...data, topic: e.target.value })}
        error={!!errors.topic}
        helperText={errors.topic || 'z.B. DT-Grundlagen'}
        fullWidth
        required
      />
      <FormControl fullWidth error={!!errors.audience}>
        <InputLabel>Zielgruppe</InputLabel>
        <Select
          value={data.audience}
          onChange={(e) => onChange({ ...data, audience: e.target.value })}
          label="Zielgruppe"
        >
          {audienceOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
        {errors.audience && <FormHelperText>{errors.audience}</FormHelperText>}
      </FormControl>
      <FormControl fullWidth error={!!errors.format}>
        <InputLabel>Formate</InputLabel>
        <Select
          multiple
          value={data.format}
          onChange={handleFormatChange}
          input={<OutlinedInput label="Formate" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {formatOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
        {errors.format && <FormHelperText>{errors.format}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export const SimulationForm: React.FC<FormProps<SimulationData>> = ({ data, onChange, errors }) => {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [machineTypes, setMachineTypes] = useState<MachineType[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [simInfo, setSimInfo] = useState<SimulationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  
  // Extended scenario options for 2025 Digital Twin Standards
  const scenarioOptions = [
    'normal', 
    'anomaly', 
    'failure', 
    'startup', 
    'shutdown',
    'corner-case', // Safety Validation
    'cyber-physical-attack', // IEC 62443
    'cascading-drift', // Kaskadierende Sensordrifte
    'ood-detection', // Out of Distribution Detection
  ];
  
  // Anomalie-Typen für KI-Training
  const anomalyTypeOptions = [
    '', // None
    'spike', // Sprunghafter Anstieg
    'drift', // Langsame Drift
    'oscillation', // Oszillation
    'step_change', // Stufenänderung
    'noise', // Erhöhtes Rauschen
    'sensor_freeze', // Eingefrorener Sensor
    'out_of_range', // Außerhalb Grenzwerte
  ];
  
  const durationOptions = ['1min', '5min', '15min', '30min', '1h', '4h', '24h'];
  const resolutionOptions = ['10ms', '100ms', '500ms', '1s', '5s', '10s', '1min'];
  
  // 2025 AAS/IDTA-konforme Ausgabeformate
  const outputFormatOptions = [
    'JSON', 
    'JSON-LD', // Semantic Web
    'AASX', // Asset Administration Shell Package
    'CSV (VDI 2770)', // VDI 2770 konform
    'OPC UA NodeSet', 
    'MQTT/Sparkplug B', // Real-time streaming
    'XML',
    'Parquet', // AI-Training optimiert
  ];
  
  // Digital Twin Fidelity Levels (ISO 23247)
  const fidelityOptions = [
    'Low (Descriptive)', // Nur beschreibend
    'Medium (Diagnostic)', // Diagnose-fähig
    'High (Operational)', // Echtzeit-Betrieb
    'Very High (Predictive)', // Prädiktiv
    'Ultra (Prescriptive)', // Handlungsempfehlungen
  ];
  
  // Zielsysteme für Digital Twin Integration
  const targetSystemOptions = [
    'Azure Digital Twins',
    'AWS IoT TwinMaker',
    'NVIDIA Omniverse',
    'MATLAB/Simulink',
    'Siemens MindSphere',
    'PTC ThingWorx',
    'Eclipse Ditto',
    'Custom AAS Server',
  ];

  // Load manufacturers on mount
  useEffect(() => {
    const loadManufacturers = async () => {
      try {
        const data = await getManufacturers();
        setManufacturers(data);
        setBackendAvailable(true);
      } catch (error) {
        console.warn('Backend not available, using fallback mode');
        setBackendAvailable(false);
      }
    };
    loadManufacturers();
  }, []);

  // Load machine types when manufacturer changes
  useEffect(() => {
    if (!data.manufacturerId) {
      setMachineTypes([]);
      return;
    }
    
    const loadMachineTypes = async () => {
      setLoading(true);
      try {
        const types = await getMachineTypes(data.manufacturerId);
        setMachineTypes(types);
      } catch (error) {
        console.error('Failed to load machine types:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMachineTypes();
  }, [data.manufacturerId]);

  // Load sensors when machine type changes
  useEffect(() => {
    if (!data.machineTypeId) {
      setSensors([]);
      return;
    }
    
    const loadSensors = async () => {
      try {
        const sensorData = await getSensors(data.machineTypeId);
        setSensors(sensorData);
      } catch (error) {
        console.error('Failed to load sensors:', error);
      }
    };
    loadSensors();
  }, [data.machineTypeId]);

  // Calculate simulation info
  useEffect(() => {
    if (!data.machineTypeId) {
      setSimInfo(null);
      return;
    }
    
    const parseDuration = (dur: string): number => {
      const match = dur.match(/(\d+)(min|h|s)/);
      if (!match) return 900;
      const value = parseInt(match[1]);
      switch (match[2]) {
        case 'h': return value * 3600;
        case 'min': return value * 60;
        case 's': return value;
        default: return 900;
      }
    };
    
    const parseResolution = (res: string): number => {
      const match = res.match(/(\d+)(ms|s)/);
      if (!match) return 1000;
      const value = parseInt(match[1]);
      return match[2] === 'ms' ? value : value * 1000;
    };
    
    const loadInfo = async () => {
      try {
        const info = await getSimulationInfo(
          data.machineTypeId!,
          parseDuration(data.duration),
          parseResolution(data.resolution)
        );
        setSimInfo(info);
      } catch (error) {
        // Calculate locally if backend fails
        const durationSec = parseDuration(data.duration);
        const resolutionMs = parseResolution(data.resolution);
        setSimInfo({
          durationSeconds: durationSec,
          resolutionMs,
          dataPointsCount: Math.floor((durationSec * 1000) / resolutionMs),
          sensorsCount: sensors.length,
          estimatedSizeKB: Math.round((Math.floor((durationSec * 1000) / resolutionMs) * sensors.length * 50) / 1024),
          availableFormats: ['json', 'xml', 'opcua', 'csv'],
        });
      }
    };
    loadInfo();
  }, [data.machineTypeId, data.duration, data.resolution, sensors.length]);

  const handleFormatChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange({ ...data, outputFormats: typeof value === 'string' ? value.split(',') : value });
  };

  const selectedMachine = machineTypes.find(m => m.id === data.machineTypeId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!backendAvailable && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Backend nicht erreichbar. Starte den Backend-Server mit <code>npm run dev</code> im backend/ Ordner.
        </Alert>
      )}

      {/* Manufacturer Selection */}
      <FormControl fullWidth disabled={!backendAvailable}>
        <InputLabel>Hersteller</InputLabel>
        <Select
          value={data.manufacturerId || ''}
          onChange={(e) => onChange({ ...data, manufacturerId: e.target.value, machineTypeId: '' })}
          label="Hersteller"
        >
          <MenuItem value="">-- Hersteller wählen --</MenuItem>
          {manufacturers.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.name} {m.country && `(${m.country})`}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>Wähle einen Hersteller aus der Datenbank</FormHelperText>
      </FormControl>

      {/* Machine Type Selection */}
      <FormControl fullWidth disabled={!data.manufacturerId || loading} error={!!errors.machineTypeId}>
        <InputLabel>Maschinentyp / Modell</InputLabel>
        <Select
          value={data.machineTypeId || ''}
          onChange={(e) => onChange({ ...data, machineTypeId: e.target.value })}
          label="Maschinentyp / Modell"
          endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
        >
          <MenuItem value="">-- Maschinentyp wählen --</MenuItem>
          {machineTypes.map((mt) => (
            <MenuItem key={mt.id} value={mt.id}>
              <Box>
                <Typography variant="body1">{mt.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {mt.model} | {mt.category}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {errors.machineTypeId && <FormHelperText>{errors.machineTypeId}</FormHelperText>}
      </FormControl>

      {/* Show selected machine info */}
      {selectedMachine && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom>
            {selectedMachine.name} - {selectedMachine.model}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Kategorie: {selectedMachine.category}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {selectedMachine.communication_protocols?.map(p => (
              <Chip key={p} label={p} size="small" variant="outlined" />
            ))}
          </Box>
          {sensors.length > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              📊 {sensors.length} Sensoren verfügbar: {sensors.slice(0, 3).map(s => s.name).join(', ')}
              {sensors.length > 3 && ` ... +${sensors.length - 3} weitere`}
            </Typography>
          )}
        </Paper>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Digital Twin Standards Section */}
      <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
        🔧 Digital Twin Konfiguration (ISO 23247 / IDTA)
      </Typography>

      {/* Fidelity Level & Target System */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Fidelity Level</InputLabel>
          <Select
            value={data.fidelity}
            onChange={(e) => onChange({ ...data, fidelity: e.target.value })}
            label="Fidelity Level"
          >
            {fidelityOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt.includes('Low') && '📊 '}
                {opt.includes('Medium') && '🔍 '}
                {opt.includes('High') && '⚡ '}
                {opt.includes('Very High') && '🎯 '}
                {opt.includes('Ultra') && '🚀 '}
                {opt}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Digital Twin Maturity Level</FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Zielsystem</InputLabel>
          <Select
            value={data.targetSystem}
            onChange={(e) => onChange({ ...data, targetSystem: e.target.value })}
            label="Zielsystem"
          >
            {targetSystemOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          <FormHelperText>Integration Platform</FormHelperText>
        </FormControl>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Scenario Selection */}
      <FormControl fullWidth error={!!errors.scenario}>
        <InputLabel>Szenario</InputLabel>
        <Select
          value={data.scenario}
          onChange={(e) => onChange({ ...data, scenario: e.target.value })}
          label="Szenario"
        >
          {scenarioOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt === 'normal' && '✅ Normalbetrieb'}
              {opt === 'anomaly' && '⚠️ Anomalie-Simulation'}
              {opt === 'failure' && '❌ Ausfall-Szenario'}
              {opt === 'startup' && '🔄 Hochfahren'}
              {opt === 'shutdown' && '⏹️ Herunterfahren'}
              {opt === 'corner-case' && '🛡️ Corner-Case (Safety Validation)'}
              {opt === 'cyber-physical-attack' && '🔐 Cyber-Physical Attack (IEC 62443)'}
              {opt === 'cascading-drift' && '📉 Kaskadierende Sensordrift'}
              {opt === 'ood-detection' && '🎲 OOD Detection (AI Training)'}
            </MenuItem>
          ))}
        </Select>
        {errors.scenario && <FormHelperText>{errors.scenario}</FormHelperText>}
      </FormControl>

      {/* Anomaly Type - only show when anomaly-related scenario is selected */}
      {(data.scenario === 'anomaly' || data.scenario === 'cascading-drift' || data.scenario === 'ood-detection') && (
        <FormControl fullWidth>
          <InputLabel>Anomalie-Typ</InputLabel>
          <Select
            value={data.anomalyType || ''}
            onChange={(e) => onChange({ ...data, anomalyType: e.target.value })}
            label="Anomalie-Typ"
          >
            {anomalyTypeOptions.map((opt) => (
              <MenuItem key={opt || 'none'} value={opt}>
                {opt === '' && '-- Automatisch wählen --'}
                {opt === 'spike' && '📈 Spike (Sprunghafter Anstieg)'}
                {opt === 'drift' && '📉 Drift (Langsame Abweichung)'}
                {opt === 'oscillation' && '〰️ Oszillation'}
                {opt === 'step_change' && '⬆️ Stufenänderung'}
                {opt === 'noise' && '📶 Erhöhtes Rauschen (Gaußsch)'}
                {opt === 'sensor_freeze' && '🧊 Eingefrorener Sensor'}
                {opt === 'out_of_range' && '⚠️ Außerhalb Operational Envelope'}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Rauschmodell für AI-Ready Daten</FormHelperText>
        </FormControl>
      )}

      {/* Duration & Resolution */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth error={!!errors.duration}>
          <InputLabel>Dauer</InputLabel>
          <Select
            value={data.duration}
            onChange={(e) => onChange({ ...data, duration: e.target.value })}
            label="Dauer"
          >
            {durationOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth error={!!errors.resolution}>
          <InputLabel>Auflösung</InputLabel>
          <Select
            value={data.resolution}
            onChange={(e) => onChange({ ...data, resolution: e.target.value })}
            label="Auflösung"
          >
            {resolutionOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Output Formats */}
      <FormControl fullWidth>
        <InputLabel>Ausgabeformate</InputLabel>
        <Select
          multiple
          value={data.outputFormats}
          onChange={handleFormatChange}
          input={<OutlinedInput label="Ausgabeformate" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {outputFormatOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt === 'JSON' && '📄 JSON (Standard)'}
              {opt === 'JSON-LD' && '🔗 JSON-LD (Semantic Web)'}
              {opt === 'AASX' && '📦 AASX (AAS Package)'}
              {opt === 'CSV (VDI 2770)' && '📊 CSV (VDI 2770)'}
              {opt === 'OPC UA NodeSet' && '🔌 OPC UA NodeSet2'}
              {opt === 'MQTT/Sparkplug B' && '📡 MQTT/Sparkplug B (Streaming)'}
              {opt === 'XML' && '📃 XML'}
              {opt === 'Parquet' && '🤖 Parquet (AI-Training)'}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>AAS/IDTA-konforme Formate für ISO 23247</FormHelperText>
      </FormControl>

      {/* Simulation Info */}
      {simInfo && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            📈 <strong>{simInfo.dataPointsCount.toLocaleString()}</strong> Datenpunkte 
            × <strong>{simInfo.sensorsCount}</strong> Sensoren
            = ca. <strong>{simInfo.estimatedSizeKB.toLocaleString()} KB</strong>
          </Typography>
        </Alert>
      )}

      {/* Synthesize Option */}
      <FormControl fullWidth>
        <InputLabel>Datengenerierung</InputLabel>
        <Select
          value={data.synthesize ? 'generate' : 'schema'}
          onChange={(e) => onChange({ ...data, synthesize: e.target.value === 'generate' })}
          label="Datengenerierung"
        >
          <MenuItem value="generate">🔢 Realistische Zeitreihen-Daten generieren</MenuItem>
          <MenuItem value="schema">📋 Nur Schema/Struktur (ohne Daten)</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export const TemplatesForm: React.FC<FormProps<TemplatesData>> = ({ data, onChange, errors }) => {
  const templateOptions = ['DITA', 'S1000D', 'AASX', 'VDI 2770', 'Markdown', 'HTML', 'LaTeX'];
  const industryOptions = ['Automotive', 'Aerospace', 'Manufacturing', 'Energy', 'Healthcare', 'Smart City'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth error={!!errors.templateType}>
        <InputLabel>Template-Typ</InputLabel>
        <Select
          value={data.templateType}
          onChange={(e) => onChange({ ...data, templateType: e.target.value })}
          label="Template-Typ"
        >
          {templateOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
        {errors.templateType && <FormHelperText>{errors.templateType}</FormHelperText>}
      </FormControl>
      <FormControl fullWidth error={!!errors.industry}>
        <InputLabel>Branche</InputLabel>
        <Select
          value={data.industry}
          onChange={(e) => onChange({ ...data, industry: e.target.value })}
          label="Branche"
        >
          {industryOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
        {errors.industry && <FormHelperText>{errors.industry}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
